import React, { useEffect, useRef } from 'react';
import { createChart } from 'lightweight-charts';

const CandlestickChart = ({ 
  data, 
  indicators = {}, 
  height = 400,
  darkMode = true 
}) => {
  const chartContainerRef = useRef();
  const chartRef = useRef();
  const seriesRef = useRef({});

  useEffect(() => {
    if (!chartContainerRef.current || !data || data.length === 0) {
      console.log('CandlestickChart: Missing container or data');
      return;
    }

    console.log('CandlestickChart: Initializing with', data.length, 'data points, darkMode:', darkMode);

    // Clean up previous chart
    if (chartRef.current) {
      console.log('CandlestickChart: Cleaning up previous chart');
      try {
        chartRef.current.remove();
      } catch (error) {
        console.log('CandlestickChart: Error removing chart:', error);
      }
      chartRef.current = null;
    }
    seriesRef.current = {};

    try {
      // Create chart with TradingView Lightweight Charts v4 API
      console.log('CandlestickChart: Creating chart...');
      
      // Helper function to parse and normalize timestamps
      const parseTimestamp = (dateStr) => {
        const dateObj = new Date(dateStr);
        return dateObj.getTime() / 1000;
      };
      
      // Helper function to deduplicate and sort data by time
      const cleanTimeSeriesData = (dataArray) => {
        return dataArray
          .sort((a, b) => a.time - b.time)
          .filter((d, index, arr) => index === 0 || d.time !== arr[index - 1].time);
      };

      const chart = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: height,
        layout: {
          backgroundColor: darkMode ? '#1f2937' : '#ffffff',
          textColor: darkMode ? '#d1d5db' : '#333333',
          fontSize: 12,
        },
        grid: {
          vertLines: { 
            color: darkMode ? '#374151' : '#e5e7eb',
            visible: true,
          },
          horzLines: { 
            color: darkMode ? '#374151' : '#e5e7eb',
            visible: true,
          },
        },
        crosshair: {
          mode: 1,
          vertLine: {
            color: darkMode ? '#9ca3af' : '#6b7280',
            width: 1,
            style: 1,
            labelBackgroundColor: darkMode ? '#374151' : '#e5e7eb',
          },
          horzLine: {
            color: darkMode ? '#9ca3af' : '#6b7280',
            width: 1,
            style: 1,
            labelBackgroundColor: darkMode ? '#374151' : '#e5e7eb',
          },
        },
        rightPriceScale: {
          borderColor: darkMode ? '#4b5563' : '#d1d5db',
          borderVisible: true,
          scaleMargins: {
            top: 0.05,
            bottom: 0.25,
          },
          autoScale: true,
          visible: true,
          mode: 0, // Normal price scale mode
        },
        leftPriceScale: {
          visible: false,
        },
        timeScale: {
          borderColor: darkMode ? '#4b5563' : '#d1d5db',
          borderVisible: true,
          timeVisible: true,
          secondsVisible: false,
          rightOffset: 5,
          barSpacing: 8,
          minBarSpacing: 3,
          fixLeftEdge: true,
          fixRightEdge: true,
        },
        localization: {
          locale: 'en-US',
          dateFormat: 'MMM dd, yyyy',
          timeFormatter: (timestamp) => {
            try {
              const date = new Date(timestamp * 1000);
              return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              });
            } catch (error) {
              return new Date(timestamp * 1000).toISOString().slice(0, 10);
            }
          },
          priceFormatter: (price) => {
            return price.toFixed(2);
          },
        },
      });

      chartRef.current = chart;

      // Add candlestick series - correct v4 API
      const candlestickSeries = chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderVisible: false,
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      });
      seriesRef.current.candlestick = candlestickSeries;

      // Prepare candlestick data
      const rawCandlestickData = data
        .map(d => ({
          time: parseTimestamp(d.date),
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }))
        .filter(d => d.open && d.high && d.low && d.close && !isNaN(d.time));
      
      const candlestickData = cleanTimeSeriesData(rawCandlestickData);
      candlestickSeries.setData(candlestickData);

      // Add technical indicators using v4 API
      if (indicators.sma5 && data[0]?.sma_5) {
        console.log('Adding SMA5 to chart');
        const sma5Series = chart.addLineSeries({
          color: '#facc15',
          lineWidth: 3,
          title: '5 SMA',
          lastValueVisible: true,
          priceLineVisible: true,
        });
        const rawSma5Data = data
          .filter(d => d.sma_5)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.sma_5,
          }));
        sma5Series.setData(cleanTimeSeriesData(rawSma5Data));
        seriesRef.current.sma5 = sma5Series;
      }

      if (indicators.sma50 && data[0]?.sma_50) {
        console.log('Adding SMA50 to chart');
        const sma50Series = chart.addLineSeries({
          color: '#22d3ee',
          lineWidth: 3,
          title: '50 SMA',
          lastValueVisible: true,
          priceLineVisible: true,
        });
        const rawSma50Data = data
          .filter(d => d.sma_50)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.sma_50,
          }));
        sma50Series.setData(cleanTimeSeriesData(rawSma50Data));
        seriesRef.current.sma50 = sma50Series;
      }

      if (indicators.sma100 && data[0]?.sma_100) {
        const sma100Series = chart.addLineSeries({
          color: '#c084fc',
          lineWidth: 2,
          title: '100 SMA',
        });
        const rawSma100Data = data
          .filter(d => d.sma_100)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.sma_100,
          }));
        sma100Series.setData(cleanTimeSeriesData(rawSma100Data));
        seriesRef.current.sma100 = sma100Series;
      }

      if (indicators.sma200 && data[0]?.sma_200) {
        const sma200Series = chart.addLineSeries({
          color: '#fb923c',
          lineWidth: 3,
          title: '200 SMA',
        });
        const rawSma200Data = data
          .filter(d => d.sma_200)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.sma_200,
          }));
        sma200Series.setData(cleanTimeSeriesData(rawSma200Data));
        seriesRef.current.sma200 = sma200Series;
      }

      // Add Bollinger Bands
      console.log('Checking Bollinger Bands:', {
        indicatorBollinger: indicators.bollinger,
        hasBollingerUpper: !!data[0]?.bollinger_upper,
        hasBbUpper: !!data[0]?.bb_upper,
        sampleData: data[0]
      });
      
      if (indicators.bollinger && (data[0]?.bollinger_upper || data[0]?.bb_upper)) {
        console.log('Adding Bollinger Bands to chart...');
        
        const bbUpperSeries = chart.addLineSeries({
          color: '#ff0000',
          lineWidth: 3,
          title: 'BB Upper',
          lastValueVisible: true,
          priceLineVisible: true,
        });
        const rawBbUpperData = data
          .filter(d => d.bollinger_upper || d.bb_upper)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.bollinger_upper || d.bb_upper,
          }));
        
        console.log('BB Upper data points:', rawBbUpperData.length);
        bbUpperSeries.setData(cleanTimeSeriesData(rawBbUpperData));
        seriesRef.current.bbUpper = bbUpperSeries;

        const bbMiddleSeries = chart.addLineSeries({
          color: '#9ca3af',
          lineWidth: 1,
          lineStyle: 2, // dashed
          title: 'BB Middle',
        });
        const rawBbMiddleData = data
          .filter(d => d.bollinger_middle || d.bb_middle)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.bollinger_middle || d.bb_middle,
          }));
        bbMiddleSeries.setData(cleanTimeSeriesData(rawBbMiddleData));
        seriesRef.current.bbMiddle = bbMiddleSeries;

        const bbLowerSeries = chart.addLineSeries({
          color: '#ff0000',
          lineWidth: 3,
          title: 'BB Lower',
          lastValueVisible: true,
          priceLineVisible: true,
        });
        const rawBbLowerData = data
          .filter(d => d.bollinger_lower || d.bb_lower)
          .map(d => ({
            time: parseTimestamp(d.date),
            value: d.bollinger_lower || d.bb_lower,
          }));
        bbLowerSeries.setData(cleanTimeSeriesData(rawBbLowerData));
        seriesRef.current.bbLower = bbLowerSeries;
      }

      // Add volume histogram (conditional based on indicators.volume)
      if (indicators.volume !== false) {
        const volumeSeries = chart.addHistogramSeries({
          color: '#26a69a',
          priceFormat: {
            type: 'volume',
          },
          priceScaleId: '', // Empty string = overlay without visible price scale
          scaleMargins: {
            top: 0.85,
            bottom: 0,
          },
          lastValueVisible: false,
          priceLineVisible: false,
        });

        const rawVolumeData = data.map(d => ({
          time: parseTimestamp(d.date),
          value: d.volume || 0,
          color: d.close >= d.open ? '#22c55e80' : '#ef444480',
        }));

        volumeSeries.setData(cleanTimeSeriesData(rawVolumeData));
        seriesRef.current.volume = volumeSeries;
      }

      // Fit content
      chart.timeScale().fitContent();

      // Handle resize
      const handleResize = () => {
        if (chartContainerRef.current && chartRef.current) {
          chartRef.current.applyOptions({
            width: chartContainerRef.current.clientWidth,
          });
        }
      };

      window.addEventListener('resize', handleResize);

      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        if (chartRef.current) {
          chartRef.current.remove();
          chartRef.current = null;
        }
      };
    } catch (error) {
      console.error('CandlestickChart: Error creating chart:', error);
      return () => {}; // Return empty cleanup function on error
    }
  }, [data, indicators, height, darkMode]);

  return (
    <div 
      ref={chartContainerRef} 
      key={`chart-${darkMode ? 'dark' : 'light'}`}
      style={{ width: '100%', height: `${height}px` }} 
    />
  );
};

export default CandlestickChart;
